import { Disclosure, Transition } from '@headlessui/react';
import {
  CalendarIcon,
  ChevronDownIcon,
  Cog6ToothIcon,
  PaperAirplaneIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { Loader2, Mail, Printer, RefreshCw, Send } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { ConfigureStrategyModal } from '@/components/treatment/v3/request/cards/followups/ConfigureStrategyModal';
import { useFollowUpTransmission } from '@/components/treatment/v3/request/cards/followups/hooks/useFollowUpTransmission';
import { SendFollowupModal } from '@/components/treatment/v3/request/cards/followups/SendFollowupModal';
import { Button } from '@/components/ui/button';
import { Z_INDEX_MODAL_NESTED } from '@/components/ui/zIndex';
import { cn } from '@/lib/utils';
import { useRefreshFaxStatusMutation } from '@/services/api/faxService';
import { treatmentRecordRequestService } from '@/services/api/treatmentRecordRequestService';

interface TaskFollowUpCardProps {
  matterId: string;
  providerId: string;
  requestId: string;
}

const formatDate = (date: Date | null | string) => {
  if (!date) return null;
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
};

const formatRelativeDate = (date: Date | null) => {
  if (!date) return null;
  const now = new Date();
  const diffMs = date.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'today';
  if (diffDays === 1) return 'tomorrow';
  if (diffDays > 0) return `in ${diffDays} days`;
  if (diffDays === -1) return 'yesterday';
  return `${Math.abs(diffDays)} days ago`;
};

const getChannelIcon = (channel: string | null | undefined) => {
  const normalized = channel?.toLowerCase() || '';
  if (normalized.includes('fax')) return <Printer className="h-4 w-4" />;
  if (normalized.includes('email')) return <Mail className="h-4 w-4" />;
  return <Send className="h-4 w-4" />;
};

const getChannelLabel = (channel: string | null | undefined) => {
  if (!channel) return 'Unknown';
  const normalized = channel.toLowerCase();
  if (normalized.includes('fax')) return 'Fax';
  if (normalized.includes('email')) return 'Email';
  if (normalized.includes('mail') || normalized.includes('letter'))
    return 'Mail';
  return channel;
};

const formatIntervalDays = (days: number | undefined): string => {
  const intervalDays = days || 3;
  return intervalDays === 1 ? '1 day' : `${intervalDays} days`;
};

type NodeStatus = 'delivered' | 'sent' | 'failed' | 'scheduled' | 'pending';

const getNodeStatus = (status: string | undefined): NodeStatus => {
  const normalized = status?.toLowerCase() || '';
  if (['delivered', 'success', 'completed'].includes(normalized))
    return 'delivered';
  if (['sent', 'pending', 'queued'].includes(normalized)) return 'sent';
  if (['failed', 'error', 'rejected'].includes(normalized)) return 'failed';
  return 'sent';
};

const getNodeColor = (status: NodeStatus): string => {
  switch (status) {
    case 'delivered':
      return 'bg-green-500';
    case 'sent':
      return 'bg-yellow-500';
    case 'failed':
      return 'bg-red-500';
    case 'scheduled':
      return 'bg-blue-400';
    case 'pending':
      return 'bg-gray-300';
    default:
      return 'bg-gray-300';
  }
};

/**
 * Task-native Follow-Up card for Medical Records tasks.
 * Displays transmission status, timeline, and actions in a clean card format.
 *
 * Features:
 * - Status badge (Auto-enabled/disabled, progress)
 * - Visual timeline showing transmission progress
 * - Collapsible history section (closed by default)
 * - Send Now and Configure actions
 */
export const TaskFollowUpCard = ({
  matterId,
  providerId,
  requestId,
}: TaskFollowUpCardProps) => {
  const [isSendModalOpen, setIsSendModalOpen] = useState(false);
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const [refreshingId, setRefreshingId] = useState<string | null>(null);

  const [refreshFaxStatus] = useRefreshFaxStatusMutation();

  // Fetch the full request
  const { data: request, isLoading: isRequestLoading } =
    treatmentRecordRequestService.useMattersProvidersRecordRequestsRetrieveQuery(
      {
        matterId,
        providerId,
        requestId,
      },
    );

  // Get follow-up transmission data
  const {
    history,
    strategy,
    totalSent,
    maxAttempts,
    isAutoEnabled,
    isComplete,
    nextScheduledDate,
    isLoading: isFollowUpLoading,
    refetchHistory,
  } = useFollowUpTransmission({
    matterId,
    providerId,
    request,
  });

  const handleRefreshStatus = async (entryId: string) => {
    setRefreshingId(entryId);
    try {
      await refreshFaxStatus(entryId).unwrap();
      await refetchHistory();
      toast.success('Status refreshed');
    } catch {
      toast.error('Failed to refresh status');
    } finally {
      setRefreshingId(null);
    }
  };

  const isLoading = isRequestLoading || isFollowUpLoading;

  // Loading state
  if (isLoading) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>Loading follow-up status...</span>
        </div>
      </div>
    );
  }

  // No request data
  if (!request) {
    return null;
  }

  // Build timeline nodes
  const sortedHistory = [...history].sort(
    (a, b) =>
      new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
  );

  type ChannelType = 'fax' | 'email' | 'mail' | 'unknown' | null;

  const normalizeChannel = (
    channel: string | null | undefined,
  ): ChannelType => {
    if (!channel) return null;
    const normalized = channel.toLowerCase();
    if (normalized.includes('fax')) return 'fax';
    if (normalized.includes('email')) return 'email';
    if (normalized.includes('mail') || normalized.includes('letter'))
      return 'mail';
    return 'unknown';
  };

  const timelineNodes: Array<{
    id: string;
    date: Date;
    label: string;
    status: NodeStatus;
    channel: ChannelType;
    isFuture: boolean;
  }> = sortedHistory.map((entry, index) => ({
    id: entry.id,
    date: new Date(entry.transmitted_at || entry.created_at),
    label: index === 0 ? 'Initial' : `#${index + 1}`,
    status: getNodeStatus(entry.status),
    channel: entry.channelIcon,
    isFuture: false,
  }));

  // Add scheduled node
  if (nextScheduledDate && isAutoEnabled && !isComplete) {
    timelineNodes.push({
      id: 'next',
      date: nextScheduledDate,
      label: 'Next',
      status: 'scheduled' as NodeStatus,
      channel: normalizeChannel(strategy?.channel),
      isFuture: true,
    });
  }

  // Add remaining pending slots
  const remainingSlots = Math.max(0, maxAttempts - timelineNodes.length);
  for (let i = 0; i < remainingSlots; i += 1) {
    timelineNodes.push({
      id: `pending-${i}`,
      date: new Date(),
      label: '',
      status: 'pending' as NodeStatus,
      channel: null,
      isFuture: true,
    });
  }

  const hasHistory = history.length > 0;

  return (
    <>
      <div className="bg-white border border-gray-200 rounded-md p-4 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-amber-50 text-amber-600">
              <PaperAirplaneIcon className="h-4 w-4" />
            </div>
            <h3 className="text-sm font-semibold text-gray-900 truncate">
              Follow-Up with Provider
            </h3>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsSendModalOpen(true)}
              className="h-8"
            >
              <PaperAirplaneIcon className="h-4 w-4 mr-1.5" />
              Send Now
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsConfigModalOpen(true)}
              className="h-8 w-8 p-0"
            >
              <Cog6ToothIcon className="h-4 w-4 text-gray-500" />
            </Button>
          </div>
        </div>

        {/* Status Badge */}
        <div
          className={cn(
            'inline-flex flex-wrap items-center gap-x-2 gap-y-1 px-3 py-1.5 rounded-md text-sm',
            isAutoEnabled
              ? 'bg-green-50 text-green-700'
              : 'bg-gray-100 text-gray-600',
          )}
        >
          <span
            className={cn(
              'h-2 w-2 rounded-full',
              isAutoEnabled ? 'bg-green-500' : 'bg-gray-400',
            )}
          />
          <span className="font-medium">
            {isAutoEnabled ? 'Automatic' : 'Manual only'}
          </span>
          <span className="text-gray-400">•</span>
          <span className="flex items-center gap-1">
            {getChannelIcon(strategy?.channel)}
            {getChannelLabel(strategy?.channel)} every{' '}
            {formatIntervalDays(strategy?.interval_days)}
          </span>
          <span className="text-gray-400">•</span>
          <span>
            {totalSent} of {maxAttempts} sent
          </span>
        </div>

        {/* Visual Timeline + Status */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 py-2">
          <div className="flex items-center gap-1">
            {timelineNodes.slice(0, 8).map((node, index) => (
              <div key={node.id} className="flex items-center">
                {index > 0 && (
                  <div
                    className={cn(
                      'w-4 h-0.5',
                      node.isFuture
                        ? 'border-t border-dashed border-gray-300'
                        : 'bg-gray-300',
                    )}
                  />
                )}
                <div
                  className={cn(
                    'h-3 w-3 rounded-full flex items-center justify-center',
                    getNodeColor(node.status),
                  )}
                  title={`${node.label}${node.isFuture ? ' (scheduled)' : ''}`}
                >
                  {node.status === 'delivered' && (
                    <CheckCircleIcon className="h-3 w-3 text-white" />
                  )}
                </div>
              </div>
            ))}
            {timelineNodes.length > 8 && (
              <span className="text-xs text-gray-400 ml-1">
                +{timelineNodes.length - 8}
              </span>
            )}
          </div>

          {/* Next Action Info */}
          {nextScheduledDate && isAutoEnabled && !isComplete && (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <CalendarIcon className="h-4 w-4 text-gray-400" />
              <span>
                Next follow-up: {formatDate(nextScheduledDate)}{' '}
                <span className="text-gray-400">
                  ({formatRelativeDate(nextScheduledDate)})
                </span>
              </span>
            </div>
          )}

          {isComplete && (
            <div className="flex items-center gap-2 text-sm text-green-600">
              <CheckCircleIcon className="h-4 w-4" />
              <span>
                Maximum follow-ups reached ({totalSent}/{maxAttempts})
              </span>
            </div>
          )}
        </div>

        {!isAutoEnabled && !hasHistory && (
          <div className="text-sm text-gray-500">
            Automatic follow-ups are disabled. Use &quot;Send Now&quot; to send
            a manual follow-up, or configure automatic follow-ups.
          </div>
        )}

        {/* Collapsible History Section */}
        {hasHistory && (
          <Disclosure>
            {({ open }) => (
              <>
                <Disclosure.Button className="flex w-full items-center justify-between text-sm text-gray-600 hover:text-gray-900 py-2 border-t border-gray-100">
                  <span className="font-medium">
                    View transmission history ({history.length})
                  </span>
                  <ChevronDownIcon
                    className={cn(
                      'h-4 w-4 transition-transform',
                      open ? 'rotate-180' : '',
                    )}
                  />
                </Disclosure.Button>
                <Transition
                  enter="transition duration-100 ease-out"
                  enterFrom="transform opacity-0"
                  enterTo="transform opacity-100"
                  leave="transition duration-75 ease-out"
                  leaveFrom="transform opacity-100"
                  leaveTo="transform opacity-0"
                >
                  <Disclosure.Panel className="space-y-2 pb-2">
                    {history.map((entry) => (
                      <div
                        key={entry.id}
                        className="flex items-center justify-between p-2 bg-gray-50 rounded-md text-sm"
                      >
                        <div className="flex items-center gap-2">
                          {getChannelIcon(entry.channelIcon)}
                          <div>
                            <div className="font-medium text-gray-900">
                              {formatDate(
                                entry.transmitted_at || entry.created_at,
                              )}
                            </div>
                            <div className="text-xs text-gray-500">
                              {(() => {
                                const normalized =
                                  entry.triggered_by?.toLowerCase() || '';
                                const isAuto =
                                  normalized === 'auto' ||
                                  normalized === 'automatic' ||
                                  normalized === 'scheduled';
                                if (entry.isInitial) {
                                  return isAuto
                                    ? 'Initial submission'
                                    : 'Manual initial submission';
                                }
                                return isAuto
                                  ? 'Automatic follow-up'
                                  : 'Manual follow-up';
                              })()}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div
                            className={cn(
                              'text-xs font-medium px-2 py-0.5 rounded',
                              getNodeStatus(entry.status) === 'delivered'
                                ? 'bg-green-100 text-green-700'
                                : getNodeStatus(entry.status) === 'sent'
                                  ? 'bg-yellow-100 text-yellow-700'
                                  : getNodeStatus(entry.status) === 'failed'
                                    ? 'bg-red-100 text-red-700'
                                    : 'bg-gray-100 text-gray-600',
                            )}
                          >
                            {entry.status || 'Sent'}
                          </div>
                          {entry.channelIcon === 'fax' &&
                            entry.fax_transmission_id && (
                              <button
                                type="button"
                                onClick={() =>
                                  handleRefreshStatus(
                                    entry.fax_transmission_id!,
                                  )
                                }
                                disabled={
                                  refreshingId === entry.fax_transmission_id
                                }
                                className="p-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-50"
                                title="Refresh fax status"
                              >
                                <RefreshCw
                                  className={cn(
                                    'h-3.5 w-3.5',
                                    refreshingId ===
                                      entry.fax_transmission_id &&
                                      'animate-spin',
                                  )}
                                />
                              </button>
                            )}
                        </div>
                      </div>
                    ))}
                  </Disclosure.Panel>
                </Transition>
              </>
            )}
          </Disclosure>
        )}
      </div>

      {/* Modals */}
      <SendFollowupModal
        isOpen={isSendModalOpen}
        onClose={() => setIsSendModalOpen(false)}
        matterId={matterId}
        providerId={providerId}
        request={request}
        zIndex={Z_INDEX_MODAL_NESTED}
        nested
      />

      <ConfigureStrategyModal
        isOpen={isConfigModalOpen}
        onClose={() => setIsConfigModalOpen(false)}
        matterId={matterId}
        providerId={providerId}
        requestId={requestId}
        currentStrategy={strategy}
        totalSent={totalSent}
        nextScheduledDate={nextScheduledDate}
        zIndex={Z_INDEX_MODAL_NESTED}
        nested
      />
    </>
  );
};
